import React, { useEffect, useState } from 'react';
import { leaderboardAPI } from '../../lib/api';
import Layout from '../../components/Layout';
import { motion } from 'framer-motion';
import { Flame, Trophy, Activity, Users, CheckCircle2 } from 'lucide-react';

export default function CoachLeaderboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    leaderboardAPI.coachView()
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <Layout title="LEADERBOARD">
        <div className="flex justify-center py-12"><div className="spinner" /></div>
      </Layout>
    );
  }

  const { leaderboard = [], active_today_count = 0, total_players = 0, date = '' } = data || {};

  return (
    <Layout title="LEADERBOARD">
      <div className="px-4 py-6 space-y-5">
        {/* Header stats */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 gap-3">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-center" data-testid="active-today-card">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Activity size={16} className="text-[#10B981]" />
              <span className="text-[10px] text-[#64748B] font-bold uppercase tracking-widest">Active Today</span>
            </div>
            <span className="font-['Barlow_Condensed'] text-3xl font-black text-[#10B981]">{active_today_count}</span>
            <span className="text-[#94A3B8] text-sm font-medium">/{total_players}</span>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 text-center" data-testid="total-players-card">
            <div className="flex items-center justify-center gap-2 mb-1">
              <Users size={16} className="text-[#2563EB]" />
              <span className="text-[10px] text-[#64748B] font-bold uppercase tracking-widest">Total Players</span>
            </div>
            <span className="font-['Barlow_Condensed'] text-3xl font-black text-[#2563EB]">{total_players}</span>
          </div>
        </motion.div>

        {/* Player list */}
        <div className="space-y-2">
          {leaderboard.map((p, i) => (
            <motion.div
              key={p.user_id}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.03 }}
              className={`bg-white border rounded-xl p-4 flex items-center gap-3 ${
                p.active_today ? 'border-[#10B981]/40' : 'border-[#E2E8F0]'
              }`}
              data-testid={`leaderboard-player-${i}`}
            >
              {/* Rank */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-black ${
                i === 0 ? 'bg-[#FF9100]/15 text-[#FF9100]' :
                i === 1 ? 'bg-[#94A3B8]/15 text-[#64748B]' :
                i === 2 ? 'bg-[#CD7F32]/15 text-[#CD7F32]' :
                'bg-[#F1F5F9] text-[#94A3B8]'
              }`}>
                {i < 3 ? <Trophy size={14} /> : p.rank}
              </div>

              {/* Player info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-['Barlow_Condensed'] text-sm font-bold truncate">{p.name}</h4>
                  {p.active_today && (
                    <span className="flex items-center gap-1 text-[9px] text-[#10B981] font-bold uppercase bg-[#10B981]/10 px-1.5 py-0.5 rounded-full">
                      <CheckCircle2 size={9} /> Today
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-3 mt-0.5">
                  {p.position && <span className="text-[9px] text-[#64748B] uppercase">{p.position}</span>}
                  <span className="text-[9px] text-[#94A3B8]">{p.total_drills} total drills</span>
                  {p.drills_today > 0 && (
                    <span className="text-[9px] text-[#10B981] font-semibold">{p.drills_today} today</span>
                  )}
                </div>
              </div>

              {/* Streak */}
              <div className="text-right flex-shrink-0">
                <div className="flex items-center gap-1 justify-end">
                  <Flame size={14} className={p.current_streak > 0 ? 'text-[#FF9100]' : 'text-[#E2E8F0]'} />
                  <span className={`font-['Barlow_Condensed'] text-lg font-black ${
                    p.current_streak > 0 ? 'text-[#FF9100]' : 'text-[#CBD5E1]'
                  }`}>
                    {p.current_streak}
                  </span>
                </div>
                <span className="text-[8px] text-[#94A3B8] uppercase tracking-wider">streak</span>
              </div>
            </motion.div>
          ))}

          {leaderboard.length === 0 && (
            <div className="text-center py-8 text-[#94A3B8] text-sm">No players yet</div>
          )}
        </div>
      </div>
    </Layout>
  );
}
